import { ClipboardCheck, Home, PackageCheck, Truck, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { OrderStatus, OrderStatusEntry } from '../../../shared/types';
import {
  digitalOrderTrackingFlow,
  getOrderTrackingIndex,
  isDigitalOrderStatus,
  orderTrackingFlow,
} from '../../../shared/utils/orderTracking';

type DeliveryTrackingStep = (typeof orderTrackingFlow)[number] | 'Cancelled';
type DigitalTrackingStep = (typeof digitalOrderTrackingFlow)[number] | 'Failed';
type DisplayTrackingStep = DeliveryTrackingStep | DigitalTrackingStep;
type TrackerState = 'completed' | 'current' | 'upcoming' | 'cancelled' | 'failed';

const deliveryTrackingSteps: DeliveryTrackingStep[] = [...orderTrackingFlow, 'Cancelled'];
const digitalTrackingSteps: DigitalTrackingStep[] = [...digitalOrderTrackingFlow, 'Failed'];

const stepIcons: Record<DisplayTrackingStep, LucideIcon> = {
  Pending: ClipboardCheck,
  Paid: Home,
  Failed: XCircle,
  Processed: ClipboardCheck,
  Shipped: PackageCheck,
  'En Route': Truck,
  Arrived: Home,
  Cancelled: XCircle,
};

type TrackerStep = {
  key: string;
  label: string;
};

const steps: TrackerStep[] = [
  { key: 'Pending', label: 'Pending' },
  { key: 'Processing', label: 'Order Payed' },
  { key: 'Shipped', label: 'In Production' },
  { key: 'En Route', label: 'Out for delivery' },
  { key: 'Delivered', label: 'Delivered' },
];

const _statusRank: Record<string, number> = steps.reduce((acc, step, idx) => {
  acc[step.key] = idx;
  return acc;
}, {} as Record<string, number>);

const _getMarker = (state: 'completed' | 'current' | 'upcoming' | 'cancelled') => {
  if (state === 'completed') return '✔';
  if (state === 'current') return '🔵';
  if (state === 'cancelled') return '❌';
  return '○';
};

const _getMarkerClass = (state: 'completed' | 'current' | 'upcoming' | 'cancelled') => {
  if (state === 'completed') return 'text-emerald-600';
  if (state === 'current') return 'text-blue-600';
  if (state === 'cancelled') return 'text-rose-600';
  return 'text-slate-400';
};

const stepDescriptions: Record<DisplayTrackingStep, string> = {
  Pending: 'Payment is being processed',
  Paid: 'Payment completed successfully',
  Failed: 'Payment did not complete',
  Processed: 'Order confirmed',
  Shipped: 'Handed to courier',
  'En Route': 'Moving to destination',
  Arrived: 'Delivered successfully',
  Cancelled: 'Order stopped',
};

const formatTimelineDate = (value: string | null): string => {
  if (!value) {
    return 'Pending';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'Pending';
  }

  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(parsedDate);
};

const getStepClasses = (state: TrackerState) => {
  if (state === 'completed') {
    return {
      card: 'border-emerald-200 bg-emerald-50/80',
      icon: 'border-emerald-500 bg-emerald-500 text-white shadow-[0_12px_24px_-18px_rgba(16,185,129,0.95)]',
      title: 'text-emerald-700',
      note: 'Reached',
      noteClass: 'text-emerald-600',
      rail: 'bg-emerald-400',
    };
  }

  if (state === 'current') {
    return {
      card: 'border-orange-300 bg-orange-50 shadow-[0_14px_30px_-22px_rgba(249,115,22,0.9)]',
      icon: 'border-orange-500 bg-orange-500 text-white shadow-[0_14px_28px_-18px_rgba(249,115,22,0.95)]',
      title: 'text-orange-700',
      note: 'Current',
      noteClass: 'text-orange-600',
      rail: 'bg-orange-400',
    };
  }

  if (state === 'cancelled' || state === 'failed') {
    return {
      card: 'border-rose-300 bg-rose-50',
      icon: 'border-rose-500 bg-rose-500 text-white shadow-[0_14px_28px_-18px_rgba(244,63,94,0.95)]',
      title: 'text-rose-700',
      note: state === 'failed' ? 'Failed' : 'Cancelled',
      noteClass: 'text-rose-600',
      rail: 'bg-rose-400',
    };
  }

  return {
    card: 'border-slate-200 bg-white',
    icon: 'border-slate-200 bg-white text-slate-300',
    title: 'text-slate-500',
    note: 'Waiting',
    noteClass: 'text-slate-400',
    rail: 'bg-slate-200',
  };
};

export const OrderStatusTracker = ({
  status,
  history,
  variant = 'compact',
}: {
  status: OrderStatus;
  history?: OrderStatusEntry[];
  variant?: 'compact' | 'detailed';
}) => {
  const isDigitalFlow =
    isDigitalOrderStatus(status) ||
    (history ?? []).some((entry) => isDigitalOrderStatus(entry.status));
  const isCancelled = status === 'Cancelled';
  const isFailed = status === 'Failed';
  const currentIndex = getOrderTrackingIndex(status, history);
  const displayTrackingFlow: DisplayTrackingStep[] = isDigitalFlow ? digitalTrackingSteps : deliveryTrackingSteps;
  const sortedHistory = [...(history ?? [])].sort(
    (left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime(),
  );

  const getStepState = (step: DisplayTrackingStep, index: number): TrackerState => {
    if (isDigitalFlow) {
      if (step === 'Failed') {
        return isFailed ? 'failed' : 'upcoming';
      }

      if (status === 'Failed') {
        return index === 0 ? 'completed' : 'upcoming';
      }

      if (index < currentIndex) {
        return 'completed';
      }

      if (index === currentIndex) {
        return 'current';
      }

      return 'upcoming';
    }

    if (step === 'Cancelled') {
      if (isCancelled) {
        return 'cancelled';
      }
      if (isFailed) {
        return 'failed';
      }
      return 'upcoming';
    }

    if (isCancelled || isFailed) {
      return index <= currentIndex ? 'completed' : 'upcoming';
    }

    if (index < currentIndex) {
      return 'completed';
    }

    if (index === currentIndex) {
      return 'current';
    }

    return 'upcoming';
  };

  const getStepTimestamp = (step: DisplayTrackingStep): string | null => {
    const matchedEntry =
      step === 'Cancelled'
        ? sortedHistory.find((entry) => entry.status === 'Cancelled' || entry.status === 'Failed')
        : sortedHistory.find((entry) => entry.status === step);

    return matchedEntry?.timestamp ?? null;
  };

  if (variant === 'detailed') {
    return (
      <div className="space-y-5">
        <div className="mx-auto max-w-md sm:hidden">
          <div className="space-y-3">
            {displayTrackingFlow.map((step, stepIndex) => {
              const StepIcon = stepIcons[step];
              const state = getStepState(step, stepIndex);
              const styles = getStepClasses(state);
              const dateLabel = formatTimelineDate(getStepTimestamp(step));

              return (
                <div
                  key={step}
                  className={`relative overflow-hidden rounded-2xl border p-4 ${styles.card}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 ${styles.icon}`}
                    >
                      <StepIcon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                        {dateLabel}
                      </p>
                      <p className={`mt-1 text-sm font-semibold ${styles.title}`}>{step}</p>
                      <p className="mt-1 text-xs text-slate-500">{stepDescriptions[step]}</p>
                      <p className={`mt-2 text-[11px] font-semibold uppercase tracking-wide ${styles.noteClass}`}>
                        {styles.note}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="hidden overflow-hidden rounded-[2rem] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbfd_100%)] px-6 py-8 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.35)] sm:block">
          <div className="relative">
            <div className="absolute left-[10%] right-[10%] top-[68px] h-[8px] rounded-full bg-slate-200/80" />
            {displayTrackingFlow.slice(0, -1).map((step, stepIndex) => {
              const state = getStepState(step, stepIndex);
              const styles = getStepClasses(state);
              const width = `${(stepIndex / (displayTrackingFlow.length - 1)) * 80}%`;

              if (state !== 'completed' && state !== 'current') {
                return null;
              }

              return (
                <div
                  key={`${step}-rail`}
                  className={`absolute left-[10%] top-[68px] h-[8px] rounded-full ${styles.rail}`}
                  style={{ width }}
                />
              );
            })}

            <div className="grid grid-cols-5 gap-3">
              {displayTrackingFlow.map((step, stepIndex) => {
                const StepIcon = stepIcons[step];
                const state = getStepState(step, stepIndex);
                const styles = getStepClasses(state);
                const dateLabel = formatTimelineDate(getStepTimestamp(step));

                return (
                  <div key={step} className="relative px-2 text-center">
                    <p className={`mb-5 text-sm font-semibold ${state === 'upcoming' ? 'text-slate-400' : styles.title}`}>
                      {dateLabel}
                    </p>
                    <div
                      className={`relative z-10 mx-auto flex h-12 w-12 items-center justify-center rounded-full border-[3px] ${styles.icon}`}
                    >
                      <StepIcon className="h-4 w-4" />
                    </div>
                    <p className={`mt-6 text-sm font-semibold ${styles.title}`}>{step}</p>
                    <p className="mt-1 min-h-[32px] text-xs leading-4 text-slate-500">{stepDescriptions[step]}</p>
                    <p className={`mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] ${styles.noteClass}`}>
                      {styles.note}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {history && history.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-xs text-slate-500">
            {history
              .slice()
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
              .map((entry) => `${entry.status} - ${new Date(entry.timestamp).toLocaleString()}`)
              .join(' | ')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[780px]">
        <div className="flex items-center gap-3">
          {displayTrackingFlow.map((step, idx) => {
            const state = getStepState(step, idx);
            const textClass =
              state === 'completed'
                ? 'text-emerald-600'
                : state === 'current'
                  ? 'text-orange-600'
                  : state === 'cancelled' || state === 'failed'
                    ? 'text-rose-600'
                    : 'text-slate-400';
            const stateLabel =
              state === 'completed'
                ? 'Done'
                : state === 'current'
                  ? 'Now'
                  : state === 'cancelled'
                    ? 'Cancelled'
                    : state === 'failed'
                      ? 'Failed'
                      : 'Next';

            return (
              <div key={step} className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] font-semibold uppercase ${textClass}`}>
                    {stateLabel}
                  </span>
                  <span className="text-xs font-semibold text-slate-700">{step}</span>
                </div>
                {idx < displayTrackingFlow.length - 1 && <div className="h-px w-10 bg-slate-200" />}
              </div>
            );
          })}
        </div>

        {history && history.length > 0 && (
          <div className="mt-2 text-xs text-slate-500">
            {history
              .slice()
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
              .map((entry) => `${entry.status} - ${new Date(entry.timestamp).toLocaleString()}`)
              .join(' | ')}
          </div>
        )}
      </div>
    </div>
  );
};
